import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
interface FormData {
  email: string;
  type: 'user' | 'merchant' | 'enterprise';
  ageConfirm: boolean;
}

const TermsPage: React.FC = () => {
  const [modalOpen, setModalOpen] = useState(false);
 const [formData, setFormData] = useState<FormData>({
       email: '',
       type: 'user',
       ageConfirm: false
     });
   const [isSubmitting, setIsSubmitting] = useState(false);
     const [submitMessage, setSubmitMessage] = useState('');

  useEffect(() => {
    document.title = 'Terms of Service — Rewardly Protocol';
    window.scrollTo(0, 0);

    const setMeta = (name: string, content: string) => {
      let el = document.querySelector(`meta[name="${name}"]`) || document.querySelector(`meta[property="${name}"]`);
      if (!el) {
        el = document.createElement('meta');
        if (name.startsWith('og:') || name.startsWith('article:')) {
          el.setAttribute('property', name);
        } else {
          el.setAttribute('name', name);
        }
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    setMeta('description', 'Terms of Service, Privacy Policy, and legal documentation for Rewardly Protocol.');
    setMeta('og:title', 'Terms of Service — Rewardly Protocol');
    setMeta('og:description', 'Legal documentation for Rewardly Protocol.');
    setMeta('og:type', 'website');
    setMeta('og:url', 'https://rewardly.live/terms');
    setMeta('og:image', 'https://rewardly.live/og-image.png');
    setMeta('og:site_name', 'Rewardly Protocol');
    setMeta('twitter:card', 'summary_large_image');
    setMeta('twitter:site', '@Rewardly_io');
    setMeta('twitter:title', 'Terms of Service — Rewardly Protocol');
    setMeta('twitter:description', 'Legal documentation for Rewardly Protocol.');
    setMeta('twitter:image', 'https://rewardly.live/og-image.png');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', 'https://rewardly.live/terms');
  }, []);

  useEffect(() => {
    if (modalOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [modalOpen]);

  const validateEmail = (email: string): boolean => {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      return emailRegex.test(email);
    };
      const handleSubmit = async (e: React.FormEvent): Promise<void> => {
        e.preventDefault();
        
        // Validate email
        if (!validateEmail(formData.email)) {
          setSubmitMessage('Please enter a valid email address');
          return;
        }
    
        // Validate age confirmation
        if (!formData.ageConfirm) {
          setSubmitMessage('Please confirm you are 18 or older');
          return;
        }
        setIsSubmitting(true);
        setSubmitMessage('');
        try {
          const response = await fetch('https://5g72iuzmj9.execute-api.us-east-1.amazonaws.com/dev/save', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ 
              email: formData.email,
              user_type: formData.type
            })
          });
    
          const raw = await response.json();
          let data: any;
          if (raw.body && typeof raw.body === 'string') {
            try { data = JSON.parse(raw.body); } catch { data = raw; }
          } else { data = raw; }
          const statusCode = raw.statusCode || response.status;
          if (statusCode === 200 && !data.error) {
            if (data.new_subscription) {
              setSubmitMessage('Welcome! You\'re on the list. Check your email for confirmation. Follow @Rewardly_io for updates.');
            } else {
              setSubmitMessage('You\'re already on the waitlist! We\'ll email you when early access opens.');
            }
            setFormData({ email: '', type: 'user', ageConfirm: false });
            setTimeout(() => {
              setModalOpen(false);
              setSubmitMessage('');
            }, 3000);
          } else {
            setSubmitMessage(data.error || 'Failed to join waitlist. Please try again.');
          }
        } catch (error) {
          console.error('Error submitting email:', error);
          setSubmitMessage('An error occurred. Please try again.');
        } finally {
          setIsSubmitting(false);
        }
      };
    
      const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
        const target = e.target as HTMLInputElement;
        const { name, value, type, checked } = target;
        setFormData(prev => ({
          ...prev,
          [name]: type === 'checkbox' ? checked : value
        }));
        // Clear error message when user starts typing
        if (submitMessage) setSubmitMessage('');
      };

  return (
    <div className="min-h-screen bg-white text-gray-900">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Crimson+Text:ital,wght@0,400;0,600;1,400&family=Outfit:wght@400;500;600;700;800&display=swap');

        :root {
          --white: #FFFFFF;
          --off-white: #F8FAFC;
          --gray-100: #F1F5F9;
          --gray-200: #E2E8F0;
          --gray-400: #94A3B8;
          --gray-500: #64748B;
          --gray-700: #475569;
          --gray-900: #0F172A;
          --blue-600: #2563EB;
          --blue-700: #1D4ED8;
        }

        * { margin: 0; padding: 0; box-sizing: border-box; }
        html { scroll-behavior: smooth; }
        
        body {
          font-family: 'Inter', -apple-system, sans-serif;
          background: var(--white);
          color: var(--gray-900);
          line-height: 1.7;
          -webkit-font-smoothing: antialiased;
        }

        h1, h2, h3, .section-title, .stat-value, .inversion-title {
          font-family: 'Outfit', 'Inter', -apple-system, sans-serif;
        }

        .container {
          max-width: 1140px;
          margin: 0 auto;
          padding: 0 24px;
        }

        .container-narrow {
          max-width: 640px;
          margin: 0 auto;
          padding: 0 24px;
        }

        /* Navigation */
        nav {
          position: fixed;
          top: 0;
          width: 100%;
          background: rgba(255,255,255,0.95);
          backdrop-filter: blur(12px);
          border-bottom: 1px solid var(--gray-200);
          z-index: 1000;
        }

        .nav-inner {
          display: flex;
          justify-content: space-between;
          align-items: center;
          height: 72px;
          max-width: 1140px;
          margin: 0 auto;
          padding: 0 24px;
        }

        .nav-menu {
          display: flex;
          align-items: center;
          gap: 32px;
        }

        .nav-menu > a, .dropdown > a {
          color: var(--gray-900);
          text-decoration: none;
          font-weight: 500;
          font-size: 15px;
          transition: color 0.2s;
          cursor: pointer;
        }

        .nav-menu > a:hover, .dropdown > a:hover { color: var(--blue-600); }

        .dropdown {
          position: relative;
        }

        .dropdown-toggle {
          display: flex;
          align-items: center;
          gap: 4px;
        }

        .dropdown-toggle::after {
          content: '▼';
          font-size: 10px;
          transition: transform 0.2s;
        }

        .dropdown:hover .dropdown-toggle::after {
          transform: rotate(180deg);
        }

        .dropdown-menu {
          position: absolute;
          top: 100%;
          left: 0;
          background: var(--white);
          border: 1px solid var(--gray-200);
          border-radius: 8px;
          padding: 8px 0;
          min-width: 180px;
          margin-top: 8px;
          opacity: 0;
          visibility: hidden;
          transform: translateY(-10px);
          transition: all 0.2s;
          box-shadow: 0 10px 40px rgba(0,0,0,0.1);
        }

        .dropdown:hover .dropdown-menu {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }

        .dropdown-menu a {
          display: block;
          padding: 10px 20px;
          color: var(--gray-900);
          text-decoration: none;
          font-size: 14px;
          transition: background 0.2s;
        }

        .dropdown-menu a:hover {
          background: var(--gray-100);
        }

        .nav-actions {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .btn {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 10px 20px;
          border-radius: 8px;
          font-weight: 600;
          font-size: 14px;
          text-decoration: none;
          transition: all 0.2s;
          border: none;
          cursor: pointer;
        }

        .btn-primary { background: var(--blue-600); color: var(--white); }
        .btn-primary:hover { background: var(--blue-700); }
        .btn-outline { background: transparent; color: var(--gray-900); border: 1px solid var(--gray-200); }
        .btn-outline:hover { border-color: var(--blue-600); color: var(--blue-600); }

        .mobile-toggle {
          display: none;
          flex-direction: column;
          gap: 5px;
          background: none;
          border: none;
          cursor: pointer;
          padding: 8px;
        }
        .mobile-toggle span { width: 22px; height: 2px; background: var(--gray-900); }

        .mobile-menu {
          display: none;
          position: fixed;
          top: 72px;
          left: 0;
          right: 0;
          background: var(--white);
          border-bottom: 1px solid var(--gray-200);
          padding: 16px 24px;
          max-height: calc(100vh - 72px);
          overflow-y: auto;
        }
        .mobile-menu.active { display: block; }
        .mobile-menu a {
          display: block;
          padding: 12px 0;
          color: var(--gray-900);
          text-decoration: none;
          font-weight: 500;
          border-bottom: 1px solid var(--gray-100);
        }
        .mobile-menu .mobile-section {
          font-size: 12px;
          color: var(--gray-500);
          text-transform: uppercase;
          margin-top: 16px;
          margin-bottom: 8px;
        }

        /* Modal */
        .modal-overlay {
          position: fixed;
          top: 0; 
          left: 0; 
          right: 0; 
          bottom: 0;
          background: rgba(0,0,0,0.5);
          z-index: 2000;
          align-items: center;
          justify-content: center;
          display: none;
        }
        .modal-overlay.active { 
          display: flex !important; 
        }

        .modal {
          background: var(--white);
          border-radius: 16px;
          padding: 32px;
          width: 100%;
          max-width: 440px;
          margin: 24px;
          box-shadow: 0 20px 60px rgba(0,0,0,0.2);
        }

        .modal h3 {
          font-size: 24px;
          font-weight: 700;
          margin-bottom: 24px;
        }

        .modal-field {
          margin-bottom: 20px;
        }

        .modal-field label {
          display: block;
          font-size: 14px;
          font-weight: 500;
          color: var(--gray-500);
          margin-bottom: 8px;
        }

        .modal-field input,
        .modal-field select {
          width: 100%;
          padding: 14px 16px;
          border: 1px solid var(--gray-200);
          border-radius: 8px;
          font-size: 15px;
          font-family: inherit;
          transition: border-color 0.2s;
        }

        .modal-field input:focus,
        .modal-field select:focus {
          outline: none;
          border-color: var(--blue-600);
        }

        .modal-checkbox {
          display: flex;
          align-items: flex-start;
          gap: 12px;
          margin-bottom: 24px;
        }

        .modal-checkbox input {
          width: 18px;
          height: 18px;
          margin-top: 2px;
          accent-color: var(--blue-600);
        }

        .modal-checkbox span {
          font-size: 14px;
          color: var(--gray-500);
          line-height: 1.5;
        }

        .modal-buttons {
          display: flex;
          gap: 12px;
        }

        .modal-buttons .btn {
          flex: 1;
          justify-content: center;
          padding: 14px 20px;
        }

        /* Hero */
        .hero {
          padding: 140px 0 60px;
          background: linear-gradient(135deg, var(--off-white) 0%, var(--white) 100%);
          border-bottom: 1px solid var(--gray-200);
          text-align: center;
        }

        .hero h1 {
          font-size: clamp(36px, 5vw, 52px);
          font-weight: 800;
          line-height: 1.1;
          margin-bottom: 16px;
        }

        .hero p {
          font-size: 18px;
          color: var(--gray-500);
          max-width: 500px;
          margin: 0 auto;
        }

        /* Content */
        .content {
          padding: 60px 0;
        }

        .section {
          margin-bottom: 48px;
          padding-bottom: 48px;
          border-bottom: 1px solid var(--gray-100);
        }

        .section:last-of-type {
          border-bottom: none;
        }

        .section h2 {
          font-size: 28px;
          font-weight: 700;
          margin-bottom: 20px;
          color: var(--gray-900);
        }

        .section p {
          font-size: 16px;
          line-height: 1.8;
          color: var(--gray-700);
          margin-bottom: 16px;
        }

        .contact-section {
          background: var(--off-white);
          border-radius: 12px;
          padding: 32px;
          margin-top: 60px;
          text-align: center;
        }

        .contact-section h3 {
          font-size: 24px;
          margin-bottom: 16px;
        }

        .contact-section p {
          color: var(--gray-500);
          margin-bottom: 24px;
        }

        .contact-links {
          display: flex;
          gap: 16px;
          justify-content: center;
          flex-wrap: wrap;
        }

        .highlight-box {
          background: var(--off-white);
          border-left: 4px solid var(--blue-600);
          padding: 24px;
          margin: 32px 0;
          border-radius: 8px;
          text-align: center;
        }

        .highlight-box h4 {
          font-size: 18px;
          font-weight: 600;
          margin-bottom: 12px;
          color: var(--gray-900);
        }

        .highlight-box p {
          margin-bottom: 8px;
          color: var(--gray-700);
        }

        /* Footer */
        footer {
          background: var(--gray-900);
          color: var(--white);
          padding: 60px 0 40px;
        }

        .footer-content {
          display: grid;
          grid-template-columns: 1fr 2fr 1fr;
          gap: 40px;
          margin-bottom: 40px;
        }

        .footer-brand h3 {
          font-size: 24px;
          margin-bottom: 12px;
        }

        .footer-brand p {
          color: rgba(255,255,255,0.6);
          font-size: 14px;
        }

        .footer-links h4 {
          font-size: 14px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 20px;
          color: rgba(255,255,255,0.5);
        }

        .footer-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 20px;
        }

        .footer-grid a {
          color: rgba(255,255,255,0.8);
          text-decoration: none;
          font-size: 14px;
          transition: color 0.2s;
        }

        .footer-grid a:hover {
          color: var(--white);
        }

        .footer-social h4 {
          font-size: 14px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
          margin-bottom: 20px;
          color: rgba(255,255,255,0.5);
        }

        .social-links {
          display: flex;
          gap: 12px;
        }

        .social-links a {
          width: 40px;
          height: 40px;
          background: rgba(255,255,255,0.1);
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          color: rgba(255,255,255,0.6);
          transition: all 0.2s;
        }

        .social-links a:hover {
          background: rgba(255,255,255,0.2);
          color: var(--white);
        }

        .footer-bottom {
          padding-top: 40px;
          border-top: 1px solid rgba(255,255,255,0.1);
          text-align: center;
          color: rgba(255,255,255,0.5);
          font-size: 14px;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .nav-menu, .nav-actions { display: none; }
          .mobile-toggle { display: flex; }
          .hero h1 { font-size: 36px; }
          .footer-content { grid-template-columns: 1fr; }
          .footer-grid { grid-template-columns: repeat(2, 1fr); }
          .contact-links { flex-direction: column; }
          .modal-buttons { flex-direction: column; }
        }
      `}</style>

        {/* Modal */}
     <div 
        className={`modal-overlay ${modalOpen ? 'active' : ''}`}
        onClick={() => setModalOpen(false)}
      >
        <div className="modal" onClick={(e) => e.stopPropagation()}>
          <h3>Join the Waitlist</h3>
          
          {submitMessage && (
            <div className={`submit-message ${submitMessage.includes('Thank you') ? 'success' : 'error'}`}>
              {submitMessage}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div className="modal-field">
              <input 
                type="email" 
                name="email"
                placeholder="Enter your email" 
                value={formData.email}
                onChange={handleInputChange}
                required 
                disabled={isSubmitting}
              />
            </div>
            <div className="modal-field">
              <label>I am a...</label>
              <select 
                name="type"
                value={formData.type}
                onChange={handleInputChange}
                required
                disabled={isSubmitting}
              >
                <option value="user">User</option>
                <option value="merchant">Merchant</option>
                <option value="enterprise">Enterprise</option>
              </select>
            </div>
            <div className="modal-checkbox">
              <input 
                type="checkbox" 
                name="ageConfirm"
                id="ageConfirm" 
                checked={formData.ageConfirm}
                onChange={handleInputChange}
                required 
                disabled={isSubmitting}
              />
              <span>I am 18 or older and agree to the <a target='_blank' rel='noopener noreferrer' href="/terms">Terms of Service and Privacy Policy</a></span>
            </div>
            <div className="modal-buttons">
              <button 
                type="submit" 
                className="btn btn-primary"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'Submitting...' : 'Join Waitlist'}
              </button>
              <button 
                type="button"
                onClick={() => setModalOpen(false)} 
                className="btn btn-outline"
                disabled={isSubmitting}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>

      <Header onJoinWaitlist={() => setModalOpen(true)} />

      {/* Hero */}
      <section className="hero">
        <div className="container-narrow">
          <h1>Terms & Privacy - Rewardly Protocol</h1>
          <p>Quick and simple. No complex legal stuff.</p>
        </div>
      </section>

      {/* Content */}
      <section className="content">
        <div className="container-narrow">
          
          {/* The Basics */}
          <div className="section">
            <h2>The Basics</h2>
            
            <p><strong>What's Rewardly Protocol?</strong> We're building protocol infrastructure to convert loyalty points into RWLY tokens that never expire. Production-ready smart contracts are in final development stages.</p>
            
            <p><strong>What's this waitlist?</strong> Just a way to stay updated. You're not buying anything, not investing, just expressing interest.</p>
            
            <p><strong>Who can join?</strong> Anyone 18 or older.</p>
          </div>

          {/* Privacy */}
          <div className="section">
            <h2>Your Privacy</h2>
            
            <p><strong>What we collect:</strong> Just your email and whether you're a user, merchant, or enterprise.</p>
            
            <p><strong>What we do:</strong> Send you updates about our progress and launch.</p>
            
            <p><strong>What we don't do:</strong> Sell your data, spam you, or share your info with others.</p>
            
            <p><strong>Your control:</strong> Unsubscribe anytime. Request your data. Ask us to delete it.</p>
          </div>

          {/* Important Notes */}
          <div className="section">
            <h2>Good to Know</h2>
            
            <p>• Everything we're building is subject to change</p>
            <p>• No tokens exist yet — they're planned for the future</p>
            <p>• This isn't financial or investment advice</p>
            <p>• We'll email you if these terms change significantly</p>
          </div>

          {/* Contact */}
          <div className="contact-section">
            <h3>Questions?</h3>
            <p>We're real people. Reach out anytime!</p>
            
            <div className="contact-links">
              <a href="mailto:hello@rewardly.live" className="btn btn-primary">Email Us</a>
              <a href="https://x.com/Rewardly_io" target="_blank" rel="noopener noreferrer" className="btn btn-outline">Follow on X</a>
              <a href="https://discord.gg/tESNkuhn6v" target="_blank" rel="noopener noreferrer" className="btn btn-outline">Join Discord</a>
            </div>
          </div>

          {/* Final Note */}
          <div className="highlight-box" style={{marginTop: '60px'}}>
            <h4>That's it!</h4>
            <p>Thanks for reading. Welcome to Rewardly Protocol!</p>
            <p style={{marginTop: '8px', fontSize: '14px', color: 'var(--gray-500)'}}>Last updated: December 8, 2025</p>
          </div>
          
        </div>
      </section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default TermsPage;